import type { CropType } from "../Configurations/CropConfiguration/CropConfigurations.js";
import type { CropVarietyConfigurations } from "../Configurations/CropConfiguration/CropVarietyConfigurations.js";
import type { Locale } from "./constants.js";
import enCA from "./locales/en-CA/cropVarieties.json" with { type: "json" };
import enUS from "./locales/en-US/cropVarieties.json" with { type: "json" };
import esMX from "./locales/es-MX/cropVarieties.json" with { type: "json" };
import frCA from "./locales/fr-CA/cropVarieties.json" with { type: "json" };
import jaJP from "./locales/ja-JP/cropVarieties.json" with { type: "json" };
import paIN from "./locales/pa-IN/cropVarieties.json" with { type: "json" };
import ptPT from "./locales/pt-PT/cropVarieties.json" with { type: "json" };

export type CropVarietyNameEntry = {
    name: string;
};

export type CropVarietiesCatalog = {
    [C in CropType]: {
        [K in keyof typeof CropVarietyConfigurations as K extends `${C}.${infer V}` ? V : never]: CropVarietyNameEntry;
    };
};

export type CropVarietyNameOverrides = {
    [C in CropType]?: { [K in keyof CropVarietiesCatalog[C]]?: CropVarietyNameEntry };
};

// JSON imports skip excess-property checks, so unknown crops and variety keys are rejected here.
type KnownKeysOnly<T> = {
    [C in keyof T]: C extends CropType
        ? { [K in keyof T[C]]: K extends keyof CropVarietiesCatalog[C] ? CropVarietyNameEntry : never }
        : never;
};

/** Complete English names; every other locale holds only the names that differ from it. */
export const CROP_VARIETY_BASE_LOCALE = "en-CA" satisfies Locale;

export const cropVarietyCatalogs = {
    "en-CA": enCA,
    "en-US": enUS,
    "fr-CA": frCA,
    "es-MX": esMX,
    "ja-JP": jaJP,
    "pt-PT": ptPT,
    "pa-IN": paIN,
} satisfies Record<Locale, CropVarietyNameOverrides> & {
    [CROP_VARIETY_BASE_LOCALE]: CropVarietiesCatalog;
    "en-US": KnownKeysOnly<typeof enUS>;
    "fr-CA": KnownKeysOnly<typeof frCA>;
    "es-MX": KnownKeysOnly<typeof esMX>;
    "ja-JP": KnownKeysOnly<typeof jaJP>;
    "pt-PT": KnownKeysOnly<typeof ptPT>;
    "pa-IN": KnownKeysOnly<typeof paIN>;
};
